import { useState } from 'react';
import { activityEvents } from '../data/activity';
import { CURRENT_USER_ID, TODAY, categories, conditions as initialConditions } from '../data/conditions';
import { loanDocuments } from '../data/documents';
import { daysPastEta, getEscalation, isResolved } from '../utils/conditions';
import type { ActivityEvent } from '../types/activity';
import type { AttentionFilter, Condition } from '../types/conditions';

export type ConditionPatch = Partial<Omit<Condition, 'id' | 'comments' | 'createdAt'>>;

type NewEvent = Omit<ActivityEvent, 'id' | 'createdAt' | 'actorId'>;

function nowIso() {
  return new Date().toISOString().slice(0, 19);
}

export function useConditions() {
  const [conditions, setConditions] = useState<Condition[]>(initialConditions);
  const [activity, setActivity] = useState<ActivityEvent[]>(activityEvents);
  const [attentionFilter, setAttentionFilter] = useState<AttentionFilter>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [commentConditionId, setCommentConditionId] = useState<string | null>(null);
  const [linkConditionId, setLinkConditionId] = useState<string | null>(null);
  /** First borrower-facing message of the session asks for confirmation. */
  const [borrowerSendConfirmed, setBorrowerSendConfirmed] = useState(false);

  const visibleConditions = conditions.filter((c) => {
    if (attentionFilter === 'overdue') return daysPastEta(c) > 0;
    if (attentionFilter === 'escalated') return getEscalation(c) !== 'none';
    return true;
  });

  const outstandingGroups = categories.map((cat) => ({
    ...cat,
    conditions: visibleConditions.filter((c) => c.category === cat.id && !isResolved(c.status)),
  }));
  const cleared = visibleConditions.filter((c) => isResolved(c.status));

  const commentCondition = conditions.find((c) => c.id === commentConditionId) ?? null;
  const commentEvents = commentCondition ? activity.filter((e) => e.conditionId === commentCondition.id) : [];

  function logEvents(events: NewEvent[]) {
    if (events.length === 0) return;
    const stamp = nowIso();
    setActivity((prev) => [
      ...prev,
      ...events.map((e, i) => ({
        ...e,
        id: `a-${Date.now()}-${i}`,
        createdAt: stamp,
        actorId: CURRENT_USER_ID,
      })),
    ]);
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll(ids: string[]) {
    setSelectedIds((prev) => {
      const allSelected = ids.every((id) => prev.has(id));
      const next = new Set(prev);
      ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  }

  function updateCondition(id: string, patch: ConditionPatch) {
    const current = conditions.find((c) => c.id === id);
    if (!current) return;

    const events: NewEvent[] = [];
    if (patch.status && patch.status !== current.status) {
      events.push({ conditionId: id, kind: 'status', from: current.status, to: patch.status });
    }
    if ('eta' in patch && patch.eta !== current.eta) {
      events.push({ conditionId: id, kind: 'eta', from: current.eta, to: patch.eta });
    }
    if (patch.assigneeId && patch.assigneeId !== current.assigneeId) {
      events.push({ conditionId: id, kind: 'owner', from: current.assigneeId, to: patch.assigneeId });
    }
    logEvents(events);

    setConditions((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        const next = { ...c, ...patch };
        if (patch.status && patch.status !== c.status) {
          next.clearedAt = isResolved(patch.status) ? TODAY : undefined;
        }
        return next;
      }),
    );
  }

  function uploadDocument(id: string, fileName: string) {
    setConditions((prev) => prev.map((c) => (c.id === id ? { ...c, documents: c.documents + 1 } : c)));
    logEvents([{ conditionId: id, kind: 'document_uploaded', fileNames: [fileName] }]);
  }

  function linkDocuments(id: string, documentIds: string[]) {
    setConditions((prev) =>
      prev.map((c) =>
        c.id === id
          ? { ...c, linkedDocumentIds: Array.from(new Set([...(c.linkedDocumentIds ?? []), ...documentIds])) }
          : c,
      ),
    );
    const fileNames = loanDocuments.filter((d) => documentIds.includes(d.id)).map((d) => d.name);
    logEvents([{ conditionId: id, kind: 'document_linked', fileNames }]);
  }

  function sendConditions(ids: string[]) {
    logEvents(ids.map((conditionId) => ({ conditionId, kind: 'conditions_sent', borrowerFacing: true })));
  }

  function addComment(id: string, body: string, mentions: string[], sentToBorrower: boolean) {
    setConditions((prev) =>
      prev.map((c) =>
        c.id === id
          ? {
              ...c,
              comments: [
                ...c.comments,
                {
                  id: `cm-${Date.now()}`,
                  authorId: CURRENT_USER_ID,
                  body,
                  createdAt: nowIso(),
                  mentions,
                  sentToBorrower,
                },
              ],
            }
          : c,
      ),
    );
  }

  return {
    conditions,
    attentionFilter,
    setAttentionFilter,
    outstandingGroups,
    cleared,
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    updateCondition,
    addComment,
    uploadDocument,
    linkDocuments,
    sendConditions,
    borrowerSendConfirmed,
    confirmBorrowerSend: () => setBorrowerSendConfirmed(true),
    linkCondition: conditions.find((c) => c.id === linkConditionId) ?? null,
    openLinkDocuments: setLinkConditionId,
    closeLinkDocuments: () => setLinkConditionId(null),
    commentCondition,
    commentEvents,
    openComments: setCommentConditionId,
    closeComments: () => setCommentConditionId(null),
  };
}
